import type { Components } from "react-markdown";
import { MarkdownCode } from "./MarkdownCode";
import { MarkdownLink } from "./MarkdownLink";

/**
 * ReactMarkdown 用の共通スタイル定義。Tailwind の preflight が見出し・リスト・テーブル等の
 * ブラウザ既定スタイルをリセットするため、素の ReactMarkdown 出力は装飾のないフラットな
 * テキストになる。markdown を描画する箇所では必ずこれ (または拡張したもの) を渡すこと。
 */
export const markdownComponents: Components = {
	h1: ({ children }) => (
		<h1 className="text-title font-semibold mt-3 mb-1.5 wrap-anywhere">{children}</h1>
	),
	h2: ({ children }) => (
		<h2 className="text-ui font-semibold mt-3 mb-1.5 wrap-anywhere">{children}</h2>
	),
	h3: ({ children }) => (
		<h3 className="text-ui font-semibold mt-2 mb-1 wrap-anywhere">{children}</h3>
	),
	p: ({ children }) => <p className="my-1.5 leading-relaxed wrap-anywhere">{children}</p>,
	ul: ({ children }) => <ul className="my-1.5 ml-4 list-disc space-y-0.5">{children}</ul>,
	ol: ({ children }) => <ol className="my-1.5 ml-4 list-decimal space-y-0.5">{children}</ol>,
	li: ({ children }) => <li className="leading-relaxed wrap-anywhere">{children}</li>,
	strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
	em: ({ children }) => <em>{children}</em>,
	hr: () => <hr className="my-3 border-line-1" />,
	blockquote: ({ children }) => (
		<blockquote className="my-2 border-l-2 border-line-1 pl-3 text-fg-2">{children}</blockquote>
	),
	code: MarkdownCode,
	pre: ({ children }) => (
		<pre className="my-2 max-h-[300px] overflow-auto whitespace-pre rounded-2 p-2 font-mono text-caption leading-normal text-fg-2 bg-surface-2 shadow-[inset_0_0_0_1px_var(--line-1)]">
			{children}
		</pre>
	),
	table: ({ children }) => (
		<div className="my-2 overflow-x-auto wrap-normal">
			<table className="w-full text-caption border-collapse">{children}</table>
		</div>
	),
	thead: ({ children }) => <thead className="border-b border-line-1">{children}</thead>,
	th: ({ children }) => <th className="px-2 py-1 text-left font-semibold">{children}</th>,
	td: ({ children }) => <td className="px-2 py-1 border-t border-line-1 text-fg-2">{children}</td>,
	a: MarkdownLink,
};

export const compactMarkdownComponents: Components = {
	...markdownComponents,
	h1: ({ children }) => (
		<h1 className="text-ui font-semibold mt-3 mb-1.5 wrap-anywhere">{children}</h1>
	),
	h2: ({ children }) => (
		<h2 className="text-caption font-semibold mt-3 mb-1.5 wrap-anywhere">{children}</h2>
	),
	h3: ({ children }) => (
		<h3 className="text-caption font-semibold mt-2 mb-1 wrap-anywhere">{children}</h3>
	),
};

/**
 * ファイルプレビュー (フルページの文書表示) 向けの拡張。チャットバブルよりも文書らしい
 * 見出し階層を付け、コードブロックの高さ制限を外す (スクロールはプレビュー全体が担う)。
 */
export const documentMarkdownComponents: Components = {
	...markdownComponents,
	h1: ({ children }) => (
		<h1 className="text-heading font-semibold mt-4 mb-2 wrap-anywhere">{children}</h1>
	),
	h2: ({ children }) => (
		<h2 className="text-title font-semibold mt-4 mb-1.5 wrap-anywhere">{children}</h2>
	),
	h3: ({ children }) => (
		<h3 className="text-ui font-semibold mt-3 mb-1 wrap-anywhere">{children}</h3>
	),
	pre: ({ children }) => (
		<pre className="my-2 overflow-x-auto whitespace-pre rounded-2 p-2 font-mono text-caption leading-normal text-fg-2 bg-surface-2 shadow-[inset_0_0_0_1px_var(--line-1)]">
			{children}
		</pre>
	),
};
